import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1773839170172-3a5a4a89de68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxxdWlldCUyMHJlYWRpbmclMjByb29tJTIwZGFya3xlbnwwfHx8fDE3NzgzMjgzMzF8MA&ixlib=rb-4.1.0&q=85";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");

  // shared form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (!res.ok) toast.error(res.error);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await register(email.trim(), password, name.trim());
    setBusy(false);
    if (!res.ok) toast.error(res.error);
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-background">
      {/* Atmospheric backdrop */}
      <div
        className="absolute inset-0 opacity-30 dark:opacity-25"
        style={{
          backgroundImage: `url(${LOGIN_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(2px) saturate(0.7)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-background/70 dark:bg-background/80" aria-hidden />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md px-6 py-10"
        data-testid="auth-card"
      >
        <div className="text-center mb-10">
          <h1 className="font-serif-journal text-5xl tracking-tight italic">journal</h1>
          <p className="mt-3 meta-label text-muted-foreground">a quiet place for thoughts</p>
        </div>

        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="grid grid-cols-2 w-full bg-transparent border border-border rounded-lg p-1">
            <TabsTrigger value="login" data-testid="tab-login" className="meta-label data-[state=active]:bg-secondary">
              sign in
            </TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register" className="meta-label data-[state=active]:bg-secondary">
              create
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="mt-8">
            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              <Input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="font-serif-journal text-base h-12 bg-transparent"
                data-testid="login-email-input"
              />
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="font-serif-journal text-base h-12 bg-transparent pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex="-1"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full h-12 meta-label"
                data-testid="login-submit-button"
              >
                {busy ? "opening…" : "enter"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register" className="mt-8">
            <form onSubmit={handleRegister} className="space-y-5" data-testid="register-form">
              <Input
                type="text"
                placeholder="name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="font-serif-journal text-base h-12 bg-transparent"
                data-testid="register-name-input"
              />
              <Input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="font-serif-journal text-base h-12 bg-transparent"
                data-testid="register-email-input"
              />
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="password (min 8 chars)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="font-serif-journal text-base h-12 bg-transparent pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex="-1"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full h-12 meta-label"
                data-testid="register-submit-button"
              >
                {busy ? "creating…" : "begin"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <p className="mt-10 text-center text-xs text-muted-foreground italic font-serif-journal">
          “the river of thought, captured one drop at a time.”
        </p>
      </motion.div>
    </div>
  );
}
